import React, { useContext, useState } from "react";
import { Box } from "@mui/material";
import { v4 } from "uuid";
import { useNavigate } from "react-router-dom";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import UserContext from "../../context/User";
import RmfDialog, { DialogActions, DialogIcon, DialogText, DialogTitle, dialogButtonSx } from "../Dialog/RmfDialog";

/**
 * Reorder a past order: put its items in the cart and open the restaurant.
 * If the cart holds another restaurant's items it asks first ("Replace your
 * cart?") and parks that cart in Carts instead of throwing it away.
 *
 *   const { reorder, busyId, dialog } = useReorder();
 *   <button onClick={() => reorder(order)} />  …  {dialog}
 *
 * Every line goes in with ONE cart write: adding them one at a time with
 * addCartItem read the same old cart each time, so only the last line stayed.
 * `onOpenRestaurant(restaurant)` replaces the plain navigation (the
 * restaurant list passes its own).
 */
export default function useReorder({ onOpenRestaurant } = {}) {
  const navigate = useNavigate();
  const { cart, cartCount, restaurant: cartRestaurantId, getCartMeta, stashCart, setCartRestaurant, updateCart } = useContext(UserContext);
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const otherCart = (order) => cartRestaurantId && String(cartRestaurantId) !== String(order.restaurant._id) && cartCount > 0;

  const putInCart = async (order) => {
    setBusyId(order._id);
    try {
      const r = order.restaurant;
      const sameShop = String(cartRestaurantId) === String(r._id);
      if (otherCart(order)) await stashCart();
      await setCartRestaurant(r._id, { _id: r._id, name: r.name, slug: r.slug, image: r.image });
      await updateCart([
        ...(sameShop ? cart : []),
        ...order.items.map((it) => ({
          key: v4(),
          _id: it.food,
          quantity: it.quantity,
          variation: { _id: it.variation?._id },
          addons: (it.addons || []).map((a) => ({ _id: a._id, options: (a.options || []).map(({ _id }) => ({ _id })) })),
          specialInstructions: "",
          menuId: null,
        })),
      ]);
      if (onOpenRestaurant) onOpenRestaurant(r, { skipCartCheck: true });
      else navigate(`/restaurant/${r.slug}`, { state: { id: r._id, name: r.name, image: r.image, slug: r.slug } });
    } finally {
      setBusyId(null);
    }
  };

  const reorder = (order) => {
    if (!order?.restaurant?._id || !order.items?.length) return;
    if (otherCart(order)) setConfirm(order);
    else putInCart(order);
  };

  const otherName = getCartMeta?.()?.name || "another restaurant";
  const dialog = (
    <RmfDialog open={Boolean(confirm)} onClose={() => setConfirm(null)}>
      <DialogIcon tone="neutral" sx={{ mb: 2.5 }}>
        <ShoppingBagOutlinedIcon />
      </DialogIcon>
      <DialogTitle besideClose={false}>Replace your cart?</DialogTitle>
      <DialogText>
        Your cart has items from {otherName}. Reordering starts a new cart from {confirm?.restaurant?.name}; your current
        cart stays saved in Carts.
      </DialogText>
      <DialogActions>
        <Box
          component="button"
          type="button"
          sx={dialogButtonSx.primary}
          onClick={() => {
            const order = confirm;
            setConfirm(null);
            putInCart(order);
          }}
        >
          Replace cart
        </Box>
        <Box component="button" type="button" sx={dialogButtonSx.secondary} onClick={() => setConfirm(null)}>
          Keep my cart
        </Box>
      </DialogActions>
    </RmfDialog>
  );

  return { reorder, busyId, dialog };
}
